import { useState } from "react";
import type { Field, Theme, FontPairing, FieldGroup } from "@structurify/shared-types";
import { MonoLabel, TypeGlyph, QuietButton } from "../components/primitives/index.ts";

interface Props {
  theme: Theme;
  fonts: FontPairing;
  fields: Field[];
  documentType?: string | null | undefined;
  onConfirm: () => void;
  onAdjust: () => void;
}

const GROUPS: FieldGroup[] = ["Header", "Insured", "Loss", "Amounts", "Adjuster", "Signature"];

export function StageConfirm({ theme, fonts, fields, documentType, onConfirm, onAdjust }: Props) {
  const [removed, setRemoved] = useState<Set<string>>(new Set());

  const visibleFields = fields.filter((f) => !removed.has(f.id));

  const toggleField = (id: string) => {
    const next = new Set(removed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setRemoved(next);
  };

  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "clamp(20px, 4vw, 48px)", overflow: "auto" }}>
      <div style={{ width: "100%", maxWidth: 880, opacity: 0, animation: "fadeUp 700ms ease-out forwards" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 18, marginBottom: 6 }}>
          <MonoLabel theme={theme} fonts={fonts} size={10}>UNDERSTOOD</MonoLabel>
          <div style={{ flex: 1, height: 1, background: theme.rule }} />
          <MonoLabel theme={theme} fonts={fonts} size={10} color={theme.inkMute}>INFERRED IN 1.8s</MonoLabel>
        </div>

        <h2
          style={{
            fontFamily: fonts.headerFamily === "serif" ? fonts.serif : fonts.sans,
            fontWeight: fonts.headerWeight,
            fontSize: 38,
            lineHeight: 1.1,
            letterSpacing: fonts.headerTracking,
            color: theme.ink,
            margin: "12px 0 0 0",
          }}
        >
          {documentType ? `${documentType}.` : "A property & casualty insurance claim form."}
        </h2>
        <p style={{ fontFamily: fonts.sans, fontSize: 15, color: theme.inkSoft, lineHeight: 1.55, marginTop: 12, marginBottom: 32, maxWidth: 620 }}>
          {documentType
            ? `${fields.length} field${fields.length !== 1 ? "s" : ""} found. You can drop any you don't need before the review.`
            : "Standard Meridian Mutual ACR-104 layout. Fourteen fields look extractable. You can drop any you don't need before the review."
          }
        </p>

        {GROUPS.map((group) => {
          const groupFields = fields.filter((f) => f.group === group);
          if (!groupFields.length) return null;
          return (
            <div key={group} style={{ marginBottom: 22 }}>
              <MonoLabel theme={theme} fonts={fonts} size={9} style={{ display: "block", marginBottom: 10 }}>
                § {group.toUpperCase()}
              </MonoLabel>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 1, background: theme.rule, border: `1px solid ${theme.rule}` }}>
                {groupFields.map((f) => {
                  const isOff = removed.has(f.id);
                  return (
                    <div
                      key={f.id}
                      onClick={() => toggleField(f.id)}
                      style={{ background: theme.panel, padding: "12px 14px", cursor: "pointer", opacity: isOff ? 0.35 : 1, transition: "opacity 200ms", display: "flex", alignItems: "center", gap: 10, position: "relative" }}
                    >
                      <TypeGlyph type={f.type} theme={theme} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.ink, fontWeight: 500, textDecoration: isOff ? "line-through" : "none" }}>
                          {f.label}
                        </div>
                        <div style={{ fontFamily: fonts.mono, fontSize: 9, color: theme.inkMute, letterSpacing: "0.05em", textTransform: "uppercase", marginTop: 2 }}>
                          {f.type.replace("_", " ")}
                        </div>
                      </div>
                      <div style={{ width: 14, height: 14, border: `1px solid ${theme.ruleStrong}`, borderRadius: 2, position: "relative", flexShrink: 0, background: isOff ? "transparent" : theme.ink }}>
                        {!isOff && (
                          <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 1, left: 1 }}>
                            <path d="M2 5 L4 7 L8 3" stroke={theme.docPaper} strokeWidth="1.5" fill="none" />
                          </svg>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 36, paddingTop: 20, borderTop: `1px solid ${theme.rule}` }}>
          <div style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkMute }}>
            <span style={{ color: theme.ink, fontWeight: 500 }}>{visibleFields.length}</span> of {fields.length} fields selected
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button onClick={onAdjust} style={{ fontFamily: fonts.sans, fontSize: 13, background: "transparent", border: "none", color: theme.inkSoft, cursor: "pointer", padding: "9px 4px" }}>
              Add a custom field
            </button>
            <QuietButton theme={theme} fonts={fonts} primary onClick={onConfirm}>
              Looks right — extract
              <svg width="11" height="11" viewBox="0 0 11 11">
                <path d="M2 5.5 L9 5.5 M6 2.5 L9 5.5 L6 8.5" stroke="currentColor" strokeWidth="1" fill="none" />
              </svg>
            </QuietButton>
          </div>
        </div>
      </div>
    </div>
  );
}
